// src/pages/sm/RosterPlanner.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Stack, MenuItem,
    IconButton, CircularProgress, Menu, ListItemIcon, ListItemText,
    Divider, Paper, Drawer, TextField, Button, Checkbox,
    FormControlLabel, Chip, Alert, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions, Fade, Tooltip
} from "@mui/material";
import {
    Refresh as ResetIcon,
    Work as WorkIcon, Bedtime as RestIcon,
    NavigateBefore, NavigateNext, Add as AddIcon, Close as CloseIcon,
    GroupAdd as GroupAddIcon, Settings as SettingsIcon
} from "@mui/icons-material";
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const RosterPlanner = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [matrix, setMatrix] = useState([]);
    const [daysInMonth, setDaysInMonth] = useState(31);
    const [shifts, setShifts] = useState([]);
    const [holidayMap, setHolidayMap] = useState({});

    const [anchorEl, setAnchorEl] = useState(null);
    const [activeCell, setActiveCell] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [smSettings, setSmSettings] = useState({ max_shifts_per_day: 1, overlap_tolerance_mins: 0 });
    const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [branches, setBranches] = useState([]);
    const [workModes, setWorkModes] = useState([]);
    const [selectedWorkMode, setSelectedWorkMode] = useState('OFFICE');
    const [workModeFilters, setWorkModeFilters] = useState([]);
    const [cellNotes, setCellNotes] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [bulkConfig, setBulkConfig] = useState({
        staffIds: [],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        daysOfWeek: [1, 2, 3, 4, 5],
        dateParity: 'ALL',
        shiftIds: [],
        workModeCode: 'OFFICE',
        notes: ''
    });

    const tenant = React.useMemo(() => localStorage.getItem('tenant'), []);

    const fetchShifts = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/shifts`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) setShifts(res.data.data);
        } catch (err) { console.error(err); }
    }, [tenant]);

    const fetchWorkModes = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/work-modes`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) setWorkModes(res.data.data);
        } catch (err) { console.error(err); }
    }, [tenant]);

    const fetchPlanner = useCallback(async () => {
        setLoading(true);
        try {
            const params = { month, year };
            if (selectedDepartment) params.org_node_id = selectedDepartment;
            if (selectedBranch) params.branch_id = selectedBranch;
            if (workModeFilters.length) params.work_mode_codes = workModeFilters.join(',');

            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/roster/planner`, {
                params,
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                setMatrix(res.data.data);
                setDaysInMonth(res.data.daysInMonth);
                setHolidayMap(res.data.holidays || {});
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [month, year, tenant, selectedDepartment, selectedBranch, workModeFilters]);

    const fetchSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/settings`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) setSmSettings(res.data.data);
        } catch (err) { console.error(err); }
    }, [tenant]);

    const fetchDepartments = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) setDepartments(buildHierarchicalList(res.data.data));
        } catch (err) { console.error(err); }
    }, [tenant]);

    const fetchBranches = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) setBranches(res.data.data);
        } catch (err) { console.error(err); }
    }, [tenant]);

    useEffect(() => {
        fetchShifts();
        fetchPlanner();
        fetchSettings();
        fetchDepartments();
        fetchBranches();
        fetchWorkModes();
    }, [fetchPlanner, fetchShifts, fetchSettings, fetchDepartments, fetchBranches, fetchWorkModes]);

    const handleMonthChange = (direction) => {
        let newMonth = month + direction;
        let newYear = year;
        if (newMonth > 12) { newMonth = 1; newYear++; }
        if (newMonth < 1) { newMonth = 12; newYear--; }
        setMonth(newMonth);
        setYear(newYear);
    };

    const onCellClick = (event, staffId, day, cellData, workType, allowSplitShift = 0) => {
        const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        setActiveCell({ staffId, day, date: dateKey, current: cellData, workType, allowSplitShift });
        setSelectedWorkMode(cellData.all_shifts[0]?.work_mode_code || 'OFFICE');
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => { setAnchorEl(null); setActiveCell(null); setSelectedWorkMode('OFFICE'); setCellNotes(''); };

    const handleUpdateCell = async (type, shiftId = null, action = 'REPLACE') => {
        if (!activeCell) return;
        try {
            const payload = { staff_id: activeCell.staffId, roster_date: activeCell.date, day_type: type, work_mode_code: selectedWorkMode, shift_id: shiftId, action, notes: cellNotes };
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/roster/cell`, payload, {
                withCredentials: true, headers: { 'X-Tenant': tenant }
            });
            fetchPlanner();
        } catch (err) { alert(err.response?.data?.message || "Error"); } finally {
            if (['REPLACE', 'RESET', 'REST'].includes(action) || type === 'REST') handleCloseMenu();
        }
    };

    const getCellColor = (dayData) => {
        if (dayData.day_type === 'LEAVE_APPROVED' || dayData.day_type === 'LEAVE') return '#e0f2fe'; // Light Blue for Leave
        if (dayData.day_type === 'HOLIDAY') return '#fef3c7'; // Amber for Holiday
        if (['REST', 'OFF_DAY', 'COMP_OFF'].includes(dayData.day_type)) return '#f1f5f9';    // Gray for OFF
        if (dayData.day_type === 'WORK') {
            return dayData.is_holiday ? '#fff7ed' : '#dcfce7'; // Lighter Amber if working on holiday, else Green
        }
        return 'transparent';
    };

    const getDayName = (day) => new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    const isWeekend = (day) => { const wd = new Date(year, month - 1, day).getDay(); return wd === 0 || wd === 6; };
    const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

    const handleSaveSettings = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/settings`, smSettings, { withCredentials: true, headers: { 'X-Tenant': tenant } });
            setSettingsOpen(false);
        } catch (err) { alert("Error saving settings"); }
    };

    const handleBulkSubmit = async (isPreview = false) => {
        if (!bulkConfig.staffIds.length || !bulkConfig.shiftIds.length) return alert("Select staff and shifts");
        setBulkLoading(true); setBulkResult(null);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/roster/bulk`, {
                staff_ids: bulkConfig.staffIds, start_date: bulkConfig.startDate, end_date: bulkConfig.endDate,
                days_of_week: bulkConfig.daysOfWeek, date_parity: bulkConfig.dateParity, shift_ids: bulkConfig.shiftIds, work_mode_code: bulkConfig.workModeCode,
                notes: bulkConfig.notes,
                is_preview: isPreview
            }, { withCredentials: true, headers: { 'X-Tenant': tenant } });
            setBulkResult({ success: true, message: res.data.message, meta: res.data.meta });
            if (!isPreview) fetchPlanner();
        } catch (err) { setBulkResult({ success: false, message: err.response?.data?.message || "Error" }); } finally { setBulkLoading(false); }
    };

    const toggleBulkStaff = (id) => setBulkConfig(p => ({ ...p, staffIds: p.staffIds.includes(id) ? p.staffIds.filter(x => x !== id) : [...p.staffIds, id] }));
    const toggleBulkShift = (id) => setBulkConfig(p => ({ ...p, shiftIds: p.shiftIds.includes(id) ? p.shiftIds.filter(x => x !== id) : [...p.shiftIds, id] }));
    const toggleBulkDayOfWeek = (day) => setBulkConfig(p => ({ ...p, daysOfWeek: p.daysOfWeek.includes(day) ? p.daysOfWeek.filter(x => x !== day) : [...p.daysOfWeek, day] }));

    const handleLogout = () => { localStorage.clear(); setIsLoggedIn(false); navigate('/login'); };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={handleLogout} />

            <Box sx={{ p: 4, maxWidth: 1600, mx: 'auto', width: '100%', flexGrow: 1 }}>
                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>Roster Board</Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Schedule management and shift overrides for shift-based staff</Typography>
                            </Box>

                            <Stack direction="row" spacing={2} alignItems="center">
                                <TextField
                                    select
                                    size="small"
                                    label="Work Mode"
                                    value={workModeFilters}
                                    onChange={(e) => setWorkModeFilters(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: selected => selected.length ? selected.join(', ') : 'All Modes'
                                    }}
                                    sx={{ minWidth: 170, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                                >
                                    {workModes.map(wm => <MenuItem key={wm.mode_code} value={wm.mode_code}>{wm.display_name}</MenuItem>)}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    label="Branch"
                                    value={selectedBranch}
                                    onChange={(e) => setSelectedBranch(e.target.value)}
                                    sx={{ minWidth: 160, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                                >
                                    <MenuItem value="">All Branches</MenuItem>
                                    {branches.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    select
                                    size="small"
                                    label="Department"
                                    value={selectedDepartment}
                                    onChange={(e) => setSelectedDepartment(e.target.value)}
                                    sx={{ minWidth: 160, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                                >
                                    <MenuItem value="">All Departments</MenuItem>
                                    {departments.map(d => (
                                        <MenuItem key={d.id} value={d.id} sx={{ pl: 2 + (d._depth || 0) * 2 }}>
                                            {d._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                            {d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <Stack direction="row" sx={{ bgcolor: '#fff', borderRadius: 3, p: 0.5, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                    <IconButton size="small" onClick={() => handleMonthChange(-1)} sx={{ borderRadius: 2.5 }}><NavigateBefore /></IconButton>
                                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 140 }}>
                                        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: 15 }}>{monthName} {year}</Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => handleMonthChange(1)} sx={{ borderRadius: 2.5 }}><NavigateNext /></IconButton>
                                </Stack>

                                <Tooltip title="System Rules">
                                    <IconButton onClick={() => setSettingsOpen(true)} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2.5, color: '#64748b' }}><SettingsIcon fontSize="small" /></IconButton>
                                </Tooltip>

                                <Button
                                    variant="contained" startIcon={<GroupAddIcon />} onClick={() => setBulkDrawerOpen(true)}
                                    sx={{ bgcolor: 'primary.main', borderRadius: 2.5, px: 3, py: 1, fontWeight: 700, textTransform: 'none', boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' } }}
                                >
                                    Bulk Schedule
                                </Button>
                            </Stack>
                        </Stack>

                        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 25px 0 rgba(0,0,0,0.04)', border: '1px solid #f1f5f9', position: 'relative' }}>
                            {loading && (
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <CircularProgress size={32} sx={{ color: 'primary.main' }} />
                                </Box>
                            )}

                            <Box sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                                <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 6 }}>
                                        <tr>
                                            <th style={{ position: 'sticky', left: 0, zIndex: 7, backgroundColor: '#f8fafc', padding: '16px', borderBottom: '2px solid #e2e8f0', minWidth: 220, textAlign: 'left', borderRight: '1px solid #e2e8f0' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', letterSpacing: '0.05em' }}>MEMBER DETAILS</Typography>
                                            </th>
                                            {[...Array(daysInMonth)].map((_, i) => {
                                                const day = i + 1;
                                                const holidayName = holidayMap[day];
                                                const isPH = !!holidayName;
                                                const isWE = isWeekend(day);
                                                
                                                return (
                                                    <th key={i} style={{ padding: '8px', borderBottom: '2px solid #e2e8f0', borderLeft: '1px solid #f1f5f9', minWidth: 48, backgroundColor: isPH ? '#fff7ed' : (isWE ? '#f1f5f9' : '#f8fafc'), textAlign: 'center' }}>
                                                        <Tooltip title={holidayName || ''}>
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: (isPH || isWE) ? '#ef4444' : '#64748b', fontSize: 10 }}>{getDayName(day)}</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 800, color: isPH ? '#ef4444' : '#1e293b' }}>{day}</Typography>
                                                            </Box>
                                                        </Tooltip>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matrix.map((row) => (
                                            <tr key={row.staff_id}>
                                                <td style={{ position: 'sticky', left: 0, zIndex: 4, backgroundColor: '#fff', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #e2e8f0', boxShadow: '4px 0 8px rgba(0,0,0,0.02)' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.25 }}>{row.full_name}</Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                                        {row.staff_code} • {row.org_node_name || row.department || 'N/A'}{row.branch_name ? ` • ${row.branch_name}` : ''}
                                                    </Typography>
                                                </td>
                                                {[...Array(daysInMonth)].map((_, i) => {
                                                    const day = i + 1;
                                                    const cell = row.days[day];
                                                    const isManual = cell.source === 'ROSTER' || cell.source === 'MANUAL';
                                                    return (
                                                        <td key={i} onClick={(e) => onCellClick(e, row.staff_id, day, cell, row.work_type, row.allow_split_shift)} style={{ padding: '4px', textAlign: 'center', borderBottom: '1px solid #f8fafc', borderLeft: '1px solid #f8fafc', cursor: 'pointer', backgroundColor: getCellColor(cell), border: cell.is_holiday ? '1px solid #fbbf24' : 'none' }}>
                                                            <Tooltip title={cell.holiday_name || ''}>
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', justifyContent: 'center', minHeight: 32 }}>
                                                                    {['REST', 'OFF_DAY', 'COMP_OFF'].includes(cell.day_type) ? (<Typography sx={{ fontSize: 11, color: isManual ? '#475569' : '#94a3b8', fontWeight: isManual ? 800 : 500 }}>OFF</Typography>) : (
                                                                        (cell.day_type === 'LEAVE_APPROVED' || cell.day_type === 'LEAVE') ? (
                                                                            <Chip label="LEAVE" size="small" sx={{ height: 18, fontSize: 8, fontWeight: 900, bgcolor: '#0ea5e9', color: '#fff' }} />
                                                                        ) : (
                                                                            (cell.all_shifts?.length > 0) ? (cell.all_shifts.map((s, idx) => {
                                                                                const wm = workModes.find(w => w.mode_code === s.work_mode_code) || {};
                                                                                return (
                                                                                    <Box key={idx} sx={{ fontSize: 10, fontWeight: 800, color: isManual ? '#fff' : 'primary.main', bgcolor: isManual ? 'primary.main' : 'transparent', border: !isManual ? '1.5px solid #e2e8f0' : 'none', px: 0.75, py: 0.25, borderRadius: 1.5, width: 'fit-content', lineHeight: 1, minWidth: 28, position: 'relative' }}>
                                                                                        {wm.short_label && wm.mode_code !== 'OFFICE' && (
                                                                                            <Box sx={{ position: 'absolute', top: -6, right: -6, bgcolor: wm.color || '#f59e0b', color: '#fff', fontSize: 7, fontWeight: 900, px: 0.5, borderRadius: 1, lineHeight: '10px', zIndex: 2 }}>
                                                                                                {wm.short_label}
                                                                                            </Box>
                                                                                        )}
                                                                                        {s.shift_code || '-'}
                                                                                    </Box>
                                                                                );
                                                                            })) : (<Box sx={{ fontSize: 10, color: '#cbd5e1' }}>-</Box>)
                                                                        )
                                                                    )}
                                                                </Box>
                                                            </Tooltip>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Paper>

                        <Stack direction="row" spacing={4} sx={{ mt: 3, px: 1 }}>
                            {[
                                { color: '#dcfce7', label: 'Scheduled Work' }, 
                                { color: '#f1f5f9', label: 'Scheduled Rest' }, 
                                { color: '#fef3c7', label: 'Public Holiday' },
                                { color: 'transparent', border: '1.5px dashed #cbd5e1', label: 'Pattern Default' },
                                ...workModes.filter(wm => wm.mode_code !== 'OFFICE').map(wm => ({
                                    color: wm.color, label: wm.display_name, isBadge: true, short_label: wm.short_label
                                }))
                            ].map((item, idx) => (
                                <Stack key={idx} direction="row" alignItems="center" spacing={1.5}>
                                    {item.isBadge ? (
                                        <Box sx={{ bgcolor: item.color, color: '#fff', fontSize: 8, fontWeight: 900, px: 0.5, borderRadius: 1, lineHeight: '12px' }}>{item.short_label}</Box>
                                    ) : (
                                        <Box sx={{ width: 14, height: 14, borderRadius: 1, bgcolor: item.color, border: item.border || 'none' }} />
                                    )}
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>{item.label}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                </Fade>
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} TransitionComponent={Fade} PaperProps={{ sx: { width: 280, borderRadius: 3, mt: 1, boxShadow: '0 10px 25px -3px rgba(0,0,0,0.1)', border: '1px solid #f1f5f9' } }}>
                <Box sx={{ px: 2.5, py: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', letterSpacing: '0.05em' }}>ADJUST SCHEDULE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>{activeCell?.day} {monthName}</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Override Notes / Reference"
                        value={cellNotes}
                        onChange={(e) => setCellNotes(e.target.value)}
                    />
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Work Mode"
                        value={selectedWorkMode}
                        onChange={(e) => setSelectedWorkMode(e.target.value)}
                    >
                        {workModes.map(wm => (
                            <MenuItem key={wm.mode_code} value={wm.mode_code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: wm.color }} />
                                    {wm.display_name}
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Divider />
                <Box sx={{ py: 1 }}>
                    {activeCell?.current?.all_shifts?.filter(s => s.shift_id).map((cs, idx) => (
                        <Box key={idx} sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{cs.shift_code}</Typography>
                            </Stack>
                            <IconButton size="small" onClick={() => handleUpdateCell('WORK', cs.shift_id, 'REMOVE')} sx={{ color: '#ef4444', bgcolor: '#fef2f2' }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                        </Box>
                    ))}
                </Box>
                <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {shifts.map(s => (
                        <MenuItem key={s.id} sx={{ py: 1, px: 2 }} onClick={() => handleUpdateCell('WORK', s.id, 'REPLACE')}>
                            <ListItemIcon sx={{ minWidth: 32 }}><WorkIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
                            <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Set as {s.shift_code}</Typography>} secondary={s.shift_name} />
                            {(activeCell?.workType === 'shift_based' || (activeCell?.workType === 'office' && Number(activeCell?.allowSplitShift || 0) === 1)) && (activeCell?.current?.all_shifts?.filter(x => x.shift_code).length < smSettings.max_shifts_per_day) && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateCell('WORK', s.id, 'APPEND'); }} sx={{ color: '#10b981', bgcolor: '#ecfdf5', ml: 1 }}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
                            )}
                        </MenuItem>
                    ))}
                </Box>
                <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
                <MenuItem sx={{ py: 1.25 }} onClick={() => handleUpdateCell('REST', null, 'REPLACE')}>
                    <ListItemIcon sx={{ minWidth: 32 }}><RestIcon fontSize="small" sx={{ color: '#64748b' }} /></ListItemIcon>
                    <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>Mark as Day OFF</Typography>} />
                </MenuItem>
                <MenuItem sx={{ py: 1.25 }} onClick={() => handleUpdateCell('RESET_TO_PATTERN', null, 'RESET')}>
                    <ListItemIcon sx={{ minWidth: 32 }}><ResetIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
                    <ListItemText primary={<Typography variant="body2" sx={{ fontWeight: 700, color: '#b45309' }}>Apply Pattern Default</Typography>} />
                </MenuItem>
            </Menu>

            <Drawer anchor="right" open={bulkDrawerOpen} onClose={() => setBulkDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, borderLeft: 'none', boxShadow: '-10px 0 25px rgba(0,0,0,0.05)' } }}>
                <Box sx={{ p: 3, bgcolor: '#0f172a', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box><Typography variant="h6" sx={{ fontWeight: 800 }}>Roster Engine</Typography><Typography variant="caption" sx={{ color: '#94a3b8' }}>Generate mass schedules with conflict detection</Typography></Box>
                    <IconButton onClick={() => setBulkDrawerOpen(false)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }}><CloseIcon /></IconButton>
                </Box>
                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1, overflowY: 'auto' }}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center' }}>
                           <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 12, mr: 1.5 }}>1</Box>
                           Timeframe Settings
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={bulkConfig.startDate} onChange={e => setBulkConfig({ ...bulkConfig, startDate: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={bulkConfig.endDate} onChange={e => setBulkConfig({ ...bulkConfig, endDate: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                        </Stack>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center' }}>
                           <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 12, mr: 1.5 }}>2</Box>
                           Weekly Cycles
                        </Typography>
                        <TextField select fullWidth size="small" label="Pattern Frequency" value={bulkConfig.dateParity} onChange={e => setBulkConfig({ ...bulkConfig, dateParity: e.target.value })} sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            <MenuItem value="ALL">Every single day</MenuItem><MenuItem value="EVEN">Alternate Even Dates</MenuItem><MenuItem value="ODD">Alternate Odd Dates</MenuItem>
                        </TextField>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', mb: 1.5, display: 'block' }}>RECUR ON WEEKDAYS</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                                <Chip key={i} label={d} size="small" color={bulkConfig.daysOfWeek.includes(i) ? "primary" : "default"} onClick={() => toggleBulkDayOfWeek(i)} sx={{ borderRadius: 1.5, fontWeight: 700, px: 0.5 }} />
                            ))}
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center' }}>
                           <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 12, mr: 1.5 }}>3</Box>
                           Assignment Details
                        </Typography>
                        <TextField label="Override Notes / Ref Doc" size="small" fullWidth value={bulkConfig.notes} onChange={e => setBulkConfig({ ...bulkConfig, notes: e.target.value })} sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                        <TextField select fullWidth size="small" value={bulkConfig.workModeCode} onChange={e => setBulkConfig({ ...bulkConfig, workModeCode: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            {workModes.map(wm => (
                                <MenuItem key={wm.mode_code} value={wm.mode_code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: wm.color }} />
                                        {wm.display_name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center' }}>
                           <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 12, mr: 1.5 }}>4</Box>
                           Shift Stacking
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {shifts.map(s => (<Chip key={s.id} label={s.shift_code} clickable color={bulkConfig.shiftIds.includes(s.id) ? "primary" : "default"} onClick={() => toggleBulkShift(s.id)} sx={{ borderRadius: 2, fontWeight: 700, py: 2 }} />))}
                        </Stack>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center' }}>
                           <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', textAlign: 'center', lineHeight: '24px', fontSize: 12, mr: 1.5 }}>5</Box>
                           Audience Selection
                        </Typography>
                        <Stack direction="row" sx={{ mb: 1 }}>
                            <Button size="small" variant="text" onClick={() => setBulkConfig({ ...bulkConfig, staffIds: matrix.map(m => m.staff_id) })} sx={{ fontWeight: 700 }}>Select All</Button>
                            <Button size="small" variant="text" color="inherit" onClick={() => setBulkConfig({ ...bulkConfig, staffIds: [] })} sx={{ fontWeight: 700 }}>Reset</Button>
                        </Stack>
                        <Paper sx={{ maxHeight: 220, overflowY: 'auto', p: 1, border: '1.5px solid #f1f5f9', borderRadius: 3, boxShadow: 'none' }}>
                            {matrix.map(m => (
                                <FormControlLabel key={m.staff_id} sx={{ display: 'flex', width: '100%', ml: 0, '&:hover': { bgcolor: '#f8fafc' }, borderBottom: '1px solid #f8fafc' }} control={<Checkbox size="small" checked={bulkConfig.staffIds.includes(m.staff_id)} onChange={() => toggleBulkStaff(m.staff_id)} color="primary" />} label={
                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                                            <Avatar sx={{ width: 26, height: 26, fontSize: 10, fontWeight: 800, bgcolor: '#e2e8f0', color: '#475569' }}>{m.full_name.charAt(0)}</Avatar>
                                            <Box><Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{m.full_name}</Typography><Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>{m.staff_code}</Typography></Box>
                                        </Stack>
                                    } />
                            ))}
                        </Paper>
                    </Box>
                    {bulkResult && (
                        <Alert severity={bulkResult.success ? "success" : "error"} sx={{ borderRadius: 3, border: '1px solid', borderColor: bulkResult.success ? '#bbf7d0' : '#fecaca' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{bulkResult.success ? "Generation Successful" : "Process Failed"}</Typography>
                            <Typography variant="caption">{bulkResult.message}</Typography>
                        </Alert>
                    )}
                </Box>
                <Box sx={{ p: 4, bgcolor: '#fff', borderTop: '1px solid #f1f5f9' }}>
                    {bulkResult?.meta?.conflictDetails?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 700, display: 'block', mb: 1 }}>Conflict Details:</Typography>
                            <Box sx={{ maxHeight: 100, overflowY: 'auto', bgcolor: '#fef2f2', p: 1, borderRadius: 1 }}>
                                {bulkResult.meta.conflictDetails.map((c, i) => <Typography key={i} variant="caption" display="block" sx={{ color: '#b91c1c' }}>- {c}</Typography>)}
                            </Box>
                        </Box>
                    )}
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" fullWidth size="large" disabled={bulkLoading || bulkConfig.staffIds.length === 0 || bulkConfig.shiftIds.length === 0} onClick={() => handleBulkSubmit(true)} sx={{ py: 1.75, fontWeight: 800, borderRadius: 3 }}>Preview</Button>
                        <Button variant="contained" fullWidth size="large" disabled={bulkLoading || bulkConfig.staffIds.length === 0 || bulkConfig.shiftIds.length === 0} onClick={() => handleBulkSubmit(false)} sx={{ py: 1.75, fontWeight: 800, borderRadius: 3, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>{bulkLoading ? <CircularProgress size={24} color="inherit" /> : 'Apply'}</Button>
                    </Stack>
                </Box>
            </Drawer>

            <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a' }}>Global Sm-Matrix Rules</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 4, borderRadius: 3 }}>These settings maintain fatigue safety and operational compliance across all shift-based departments.</Alert>
                    <Stack spacing={4}>
                        <Box><Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Staff Stacking Limit</Typography><TextField fullWidth type="number" value={smSettings.max_shifts_per_day} onChange={(e) => setSmSettings({ ...smSettings, max_shifts_per_day: e.target.value })} helperText="Max shifts a single member can execute within a 24-hour window." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} /></Box>
                        <Box sx={{ opacity: 0.6 }}><Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Overlap Buffer (Minutes)</Typography><TextField fullWidth disabled type="number" value={smSettings.overlap_tolerance_mins} helperText="Grace period allowed between consecutive shifts. Currently hardcoded to 0." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} /></Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setSettingsOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Discard</Button>
                    <Button onClick={handleSaveSettings} variant="contained" sx={{ bgcolor: 'primary.main', borderRadius: 2.5, px: 3, fontWeight: 700, '&:hover': { bgcolor: 'primary.dark' } }}>Save Configuration</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RosterPlanner;
